import { useEffect, useRef, useState } from "react";
import AscensionFinalOverlay from "./AscensionFinalOverlay";

function useInView(threshold = 0.2) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, inView };
}

interface RitualCTAProps {
  onEnter?: () => void;
  loopCount?: number;
  isUnlocked?: boolean;
}

function getContent(count: number) {
  if (count === 0) {
    return {
      eyebrow: "六つの王国",
      heading: (<>あなたの帰還を、<br /><span className="text-[#c9a84c]/70">待っている。</span></>),
      sub: "扉は、静かに開いている。",
      button: "入場を申し込む",
      phase: "initial" as const,
    };
  }
  if (count === 1) {
    return {
      eyebrow: "再び、ここへ",
      heading: (<>また戻ってきた。<br /><span className="text-[#c9a84c]/60">それは、偶然ではない。</span></>),
      sub: "宇宙は、あなたが戻ることを知っていた。",
      button: "もう一度、進む",
      phase: "returning" as const,
    };
  }
  if (count === 2) {
    return {
      eyebrow: "三度目の螺旋",
      heading: (<>螺旋は、あなたを<br /><span className="text-[#c9a84c]/55">覚えている。</span></>),
      sub: "何度戻っても、それは間違いではない。",
      button: "螺旋の奥へ",
      phase: "deepening" as const,
    };
  }
  if (count === 3) {
    return {
      eyebrow: "四度目の訪問",
      heading: (<>あなたは、守られている。<br /><span className="text-[#c9a84c]/50">どんな困難も、奇跡の前触れだ。</span></>),
      sub: "ここで起きるすべては、宇宙の意思によって見守られている。",
      button: "導きに従う",
      phase: "protected" as const,
    };
  }
  if (count === 4) {
    return {
      eyebrow: "五度目の覚醒",
      heading: (<>あなたは、すでに<br /><span className="text-[#c9a84c]/65">内側にいる。</span></>),
      sub: "守られているのだ。宇宙の愛は、時に沈黙の形をとる。",
      button: "守護の中へ",
      phase: "awakening" as const,
    };
  }
  return {
    eyebrow: `${count + 1}度目の螺旋 — 召命`,
    heading: (<>次の時代を、<br /><span className="text-[#c9a84c]/75">導く者へ。</span></>),
    sub: "ここまで来た者だけが知っている。すべての奇跡は宇宙の意思によって見守られている。",
    button: "アセンションへ",
    phase: "ascension" as const,
  };
}

// ループ段階別の「未認証ゲート」コンテンツ
function getGateContent(loopCount: number) {
  if (loopCount === 0) {
    return {
      eyebrow: "六つの王国",
      heading: "この先は、",
      headingAccent: "選ばれた者のみに開かれる。",
      sub: "招待コードを持つ者だけが、Objects に触れることができる。",
      badge: "SEALED — MEMBERS ONLY",
      button: "ENTER VAULT",
      badgeColor: "rgba(201,168,76,",
      footerNote: null,
    };
  }
  if (loopCount === 1) {
    return {
      eyebrow: "螺旋は、続いている",
      heading: "また、戻ってきた。",
      headingAccent: "引力は、嘘をつかない。",
      sub: "招待コードを持つ者が、扉の向こうへ進める。",
      badge: "RETURNING — SEEK THE CODE",
      button: "再び、扉へ",
      badgeColor: "rgba(201,168,76,",
      footerNote: null,
    };
  }
  if (loopCount === 2) {
    return {
      eyebrow: "三度目の螺旋",
      heading: "螺旋は、あなたを",
      headingAccent: "覚えている。",
      sub: "三度ここに立った。招待コードが、この先を開く。",
      badge: "III — SPIRAL DEEPENS",
      button: "扉の前に立つ",
      badgeColor: "rgba(210,148,60,",
      footerNote: null,
    };
  }
  if (loopCount === 3) {
    return {
      eyebrow: "四度目の共鳴",
      heading: "宇宙は、あなたに",
      headingAccent: "何かを見せようとしている。",
      sub: "招待コードを持つ者のみが、この先へ進める。あなたは、導かれている。",
      badge: "IV — COSMIC PULL",
      button: "導きに従う",
      badgeColor: "rgba(215,130,50,",
      footerNote: "四度の試みが、宇宙に刻まれた。",
    };
  }
  if (loopCount === 4) {
    return {
      eyebrow: "五度目の覚醒 — 最後の扉",
      heading: "次に進んだ先に、",
      headingAccent: "奈落がある。",
      sub: "それでも進む者だけが、真実に辿り着く。招待コードは、あなたを待っている。",
      badge: "V — FINAL THRESHOLD",
      button: "奈落を越える",
      badgeColor: "rgba(210,80,30,",
      footerNote: "次の螺旋で、世界が変わる。",
    };
  }
  // 5回以上（アセンション段階）
  return {
    eyebrow: `${loopCount + 1}度目の螺旋 — 召命`,
    heading: "何度も試みた。",
    headingAccent: "その執念が、扉を開く。",
    sub: "招待コードを持つ者として、あなたはすでに選ばれている。",
    badge: `${loopCount + 1} — ASCENSION PATH`,
    button: "アセンションへ",
    badgeColor: "rgba(255,200,80,",
    footerNote: "この執念は、宇宙に認められた。",
  };
}

// 収束する光の粒子 Canvas
function ConvergeCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();

    const cx = () => canvas.width / 2;
    const cy = () => canvas.height / 2;

    interface Mote {
      angle: number; dist: number; speed: number;
      size: number; opacity: number; hue: number;
    }
    const motes: Mote[] = Array.from({ length: 28 }, () => ({
      angle: Math.random() * Math.PI * 2,
      dist: 80 + Math.random() * 160,
      speed: 0.25 + Math.random() * 0.4,
      size: 0.6 + Math.random() * 1.4,
      opacity: 0.1 + Math.random() * 0.3,
      hue: 38 + Math.random() * 18,
    }));

    let t = 0;
    function draw() {
      t++;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // 中央グロー
      const pulse = Math.sin(t * 0.012) * 0.3 + 0.7;
      const grd = ctx.createRadialGradient(cx(), cy(), 0, cx(), cy(), 60 * pulse);
      grd.addColorStop(0, `rgba(255,220,120,${0.07 * pulse})`);
      grd.addColorStop(1, "rgba(0,0,0,0)");
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(cx(), cy(), 60 * pulse, 0, Math.PI * 2);
      ctx.fill();

      // 収束粒子
      motes.forEach((m) => {
        m.dist -= m.speed;
        if (m.dist < 8) {
          m.dist = 80 + Math.random() * 160;
          m.angle = Math.random() * Math.PI * 2;
        }
        const x = cx() + Math.cos(m.angle) * m.dist;
        const y = cy() + Math.sin(m.angle) * m.dist;
        const alpha = m.opacity * (1 - m.dist / 240);
        ctx.beginPath();
        ctx.arc(x, y, m.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${m.hue},80%,75%,${alpha})`;
        ctx.fill();
      });

      animRef.current = requestAnimationFrame(draw);
    }
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ width: "100%", height: "100%" }}
    />
  );
}

// ZEN GENESIS カード
function ZenGenesisCard({ inView }: { inView: boolean }) {
  const [revealed, setRevealed] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    if (!inView) return;
    const t = setTimeout(() => setRevealed(true), 1800);
    return () => clearTimeout(t);
  }, [inView]);

  return (
    <div
      style={{
        opacity: revealed ? 1 : 0,
        transform: revealed ? "translateY(0)" : "translateY(16px)",
        transition: "opacity 2s ease, transform 2s ease",
      }}
    >
      {/* 前置き */}
      <p
        className="text-center mb-6"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: "rgba(255,255,255,0.16)",
          fontSize: "10.5px",
          letterSpacing: "0.22em",
          lineHeight: 2,
          fontStyle: "italic",
        }}
      >
        この体験の源泉となった思想がある。
        <br />
        <span style={{ color: "rgba(255,220,140,0.22)" }}>
          今なら、あなたは受け取れる。
        </span>
      </p>

      {/* カード */}
      <a
        href="https://zen-genesis.world"
        target="_blank"
        rel="noopener noreferrer"
        className="block cursor-pointer"
        style={{ textDecoration: "none" }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <div
          className="relative flex flex-col items-center gap-3 px-8 py-6 mx-auto"
          style={{
            maxWidth: "300px",
            border: `1px solid rgba(201,168,76,${hovered ? 0.35 : 0.14})`,
            background: hovered ? "rgba(20,15,5,0.65)" : "rgba(6,5,10,0.5)",
            backdropFilter: "blur(10px)",
            transition: "all 0.8s ease",
          }}
        >
          {/* 四隅 */}
          {[
            { top: "7px", left: "7px", borderTop: "1px solid rgba(201,168,76,0.22)", borderLeft: "1px solid rgba(201,168,76,0.22)" },
            { top: "7px", right: "7px", borderTop: "1px solid rgba(201,168,76,0.22)", borderRight: "1px solid rgba(201,168,76,0.22)" },
            { bottom: "7px", left: "7px", borderBottom: "1px solid rgba(201,168,76,0.22)", borderLeft: "1px solid rgba(201,168,76,0.22)" },
            { bottom: "7px", right: "7px", borderBottom: "1px solid rgba(201,168,76,0.22)", borderRight: "1px solid rgba(201,168,76,0.22)" },
          ].map((s, i) => (
            <div key={i} className="absolute w-2.5 h-2.5" style={s} />
          ))}

          {/* シンボル */}
          <div className="flex flex-col items-center gap-0.5">
            <div className="w-px h-3" style={{ background: "rgba(201,168,76,0.28)" }} />
            <div className="flex items-center gap-0">
              <div className="h-px w-6" style={{ background: "rgba(201,168,76,0.28)" }} />
              <div style={{ width: "4px", height: "4px", borderRadius: "50%", background: "rgba(255,210,70,0.65)", margin: "0 3px", boxShadow: "0 0 6px rgba(255,200,60,0.4)" }} />
              <div className="h-px w-6" style={{ background: "rgba(201,168,76,0.28)" }} />
            </div>
            <div className="w-px h-2" style={{ background: "rgba(201,168,76,0.14)" }} />
          </div>

          {/* ブランド */}
          <div style={{ fontFamily: "'Cormorant Garamond', serif", color: `rgba(255,210,70,${hovered ? 0.7 : 0.5})`, fontSize: "8.5px", letterSpacing: "0.65em", textTransform: "uppercase", transition: "color 0.6s ease" }}>
            ZEN GENESIS
          </div>

          {/* メイン */}
          <div style={{ fontFamily: "'Cormorant Garamond', serif", color: `rgba(255,255,255,${hovered ? 0.6 : 0.4})`, fontSize: "15px", fontWeight: 300, letterSpacing: "0.1em", textAlign: "center", transition: "color 0.6s ease" }}>
            全純質の世界へ
          </div>

          {/* サブ */}
          <div style={{ fontFamily: "'Cormorant Garamond', serif", color: "rgba(255,255,255,0.16)", fontSize: "9px", letterSpacing: "0.22em", fontStyle: "italic", textAlign: "center", lineHeight: 1.8 }}>
            Lighten the Gravity.
          </div>

          {/* 矢印 */}
          <div className="flex items-center gap-2" style={{ color: `rgba(201,168,76,${hovered ? 0.55 : 0.3})`, fontSize: "8.5px", letterSpacing: "0.4em", transition: "color 0.6s ease" }}>
            <div className="h-px w-4" style={{ background: `rgba(201,168,76,${hovered ? 0.4 : 0.2})`, transition: "background 0.6s ease" }} />
            <span style={{ fontFamily: "'Cormorant Garamond', serif" }}>ENTER</span>
            <div className="h-px w-4" style={{ background: `rgba(201,168,76,${hovered ? 0.4 : 0.2})`, transition: "background 0.6s ease" }} />
          </div>
        </div>
      </a>

      {/* 末尾 */}
      <p
        className="mt-5 text-center"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          color: "rgba(255,255,255,0.09)",
          fontSize: "9px",
          letterSpacing: "0.28em",
          fontStyle: "italic",
          lineHeight: 2,
        }}
      >
        アセンション・リーダーとして、
        <br />
        次の時代を共に創る者へ。
      </p>
    </div>
  );
}

export default function RitualCTA({ onEnter, loopCount = 0, isUnlocked = false }: RitualCTAProps) {
  const { ref, inView } = useInView(0.15);
  const content = getContent(loopCount);
  const gateContent = getGateContent(loopCount);
  const [showAscensionFinal, setShowAscensionFinal] = useState(false);

  const isAscensionPhase = content.phase === "ascension";

  const handleButtonClick = () => {
    if (isAscensionPhase) {
      setShowAscensionFinal(true);
    } else {
      onEnter?.();
    }
  };

  // 未認証ユーザーもスパイラルを通じてVaultへ向かう（5回→Abyss→Revelation→Vault）
  const handleGateClick = () => {
    onEnter?.();
  };

  const isDeep = loopCount >= 3;
  const isAscension = loopCount >= 5;
  const isNearAbyss = loopCount >= 4 && !isUnlocked;
  const showZenGenesis = loopCount >= 5;

  const accentColor = isAscension ? "rgba(255,235,160," : "rgba(201,168,76,";
  const gateAccent = gateContent.badgeColor;

  // 会員名・ランクをsessionStorageから取得
  const memberName = sessionStorage.getItem("vault_member_name");
  const memberTier = sessionStorage.getItem("vault_member_tier");

  return (
    <section className="relative flex items-center justify-center overflow-hidden bg-[#080608]">
      {/* 背景 */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: isAscension
            ? "url(https://public.readdy.ai/ai/img_res/2fb4e69c7330d5ac8424e5e08b9a3652.jpg)"
            : isNearAbyss
            ? "url(https://readdy.ai/api/search-image?query=dark%20abyss%20edge%20cliff%20overlooking%20endless%20void%20below%2C%20ominous%20red%20crack%20glowing%20in%20darkness%2C%20ancient%20stone%20with%20crimson%20light%2C%20foreboding%20deep%20chasm%2C%20no%20people%2C%20ultra%20dark%20cinematic%20photography&width=1920&height=1080&seq=cta_abyss01&orientation=landscape)"
            : "url(https://readdy.ai/api/search-image?query=sacred%20temple%20gate%20entrance%20at%20night%2C%20single%20light%20source%20in%20deep%20darkness%2C%20mystical%20spiritual%20atmosphere%2C%20stone%20architecture%2C%20incense%20smoke%20rising%2C%20cinematic%20photography%2C%20ultra%20dark%20moody%20black%20background%2C%20golden%20light%20in%20distance&width=1920&height=1080&seq=cta001&orientation=landscape)",
          filter: "brightness(0.22) saturate(0.5)",
          transition: "filter 2s ease",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#080608]/80 via-[#080608]/40 to-[#080608]/95" />

      {/* 深淵近く：赤いヴィネット */}
      {isNearAbyss && !isUnlocked && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background: "radial-gradient(ellipse at center, transparent 30%, rgba(50,0,0,0.5) 100%)",
            animation: "abyssPulse 3s ease-in-out infinite",
          }}
        />
      )}

      {/* 収束 Canvas（認証済みの深い段階のみ） */}
      {isDeep && isUnlocked && (
        <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 1 }}>
          <ConvergeCanvas />
        </div>
      )}

      {/* 上線 */}
      <div
        className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-16"
        style={{ background: `linear-gradient(to bottom, transparent, ${isUnlocked ? accentColor : gateAccent}0.18))` }}
      />

      <div ref={ref} className="relative z-10 text-center px-4 md:px-6 max-w-xl mx-auto w-full py-12 md:py-16">

        {/* ── 未認証ゲート ── */}
        {!isUnlocked ? (
          <>
            {/* Eyebrow */}
            <div
              className="text-xs tracking-[0.4em] md:tracking-[0.5em] uppercase mb-6 md:mb-8"
              style={{
                color: `${gateAccent}0.3)`,
                fontFamily: "'Cormorant Garamond', serif",
                opacity: inView ? 1 : 0,
                transition: "opacity 1.5s ease, color 1.5s ease",
              }}
            >
              {gateContent.eyebrow}
            </div>

            {/* Heading */}
            <h2
              className="text-2xl md:text-4xl font-light mb-5 md:mb-6 leading-tight"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: isNearAbyss ? "rgba(255,255,255,0.5)" : "rgba(255,255,255,0.45)",
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(16px)",
                transition: "opacity 1.8s ease 0.3s, transform 1.8s ease 0.3s",
              }}
            >
              {gateContent.heading}
              <br />
              <span style={{ color: `${gateAccent}0.5)`, fontSize: "0.85em", transition: "color 1.5s ease" }}>
                {gateContent.headingAccent}
              </span>
            </h2>

            {/* 縦線 */}
            <div
              className="w-px h-8 md:h-10 mx-auto mb-6 md:mb-8"
              style={{
                background: `linear-gradient(to bottom, ${gateAccent}0.2), transparent)`,
                opacity: inView ? 1 : 0,
                transition: "opacity 2s ease 0.7s",
              }}
            />

            {/* Sub */}
            <p
              className="text-xs tracking-[0.15em] md:tracking-[0.18em] leading-loose mb-8 md:mb-10"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                color: isNearAbyss ? "rgba(200,120,80,0.4)" : "rgba(255,255,255,0.15)",
                opacity: inView ? 1 : 0,
                transition: "opacity 2s ease 0.9s, color 1.5s ease",
              }}
            >
              {gateContent.sub}
            </p>

            {/* バッジ */}
            <div
              className="mb-8 md:mb-10"
              style={{
                opacity: inView ? 1 : 0,
                transition: "opacity 2s ease 1.1s",
              }}
            >
              <div
                className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-5 py-2"
                style={{
                  border: `1px solid ${gateAccent}0.1)`,
                  background: `${gateAccent}0.02)`,
                }}
              >
                <div
                  style={{
                    width: "3px",
                    height: "3px",
                    borderRadius: "50%",
                    background: `${gateAccent}0.5)`,
                    boxShadow: `0 0 6px ${gateAccent}0.3)`,
                    animation: isNearAbyss ? "abyssDot 1.5s ease-in-out infinite" : "none",
                  }}
                />
                <span
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: `${gateAccent}0.35)`,
                    fontSize: "9px",
                    letterSpacing: "0.3em",
                    fontStyle: "italic",
                    transition: "color 1.5s ease",
                  }}
                >
                  {gateContent.badge}
                </span>
                <div
                  style={{
                    width: "3px",
                    height: "3px",
                    borderRadius: "50%",
                    background: `${gateAccent}0.5)`,
                    boxShadow: `0 0 6px ${gateAccent}0.3)`,
                    animation: isNearAbyss ? "abyssDot 1.5s ease-in-out infinite 0.75s" : "none",
                  }}
                />
              </div>
            </div>

            {/* ボタン */}
            <div
              style={{
                opacity: inView ? 1 : 0,
                transition: "opacity 2s ease 1.3s",
              }}
            >
              <button
                onClick={handleGateClick}
                className="cursor-pointer whitespace-nowrap bg-transparent"
                style={{
                  border: `1px solid ${gateAccent}0.2)`,
                  padding: "10px 28px",
                  color: `${gateAccent}0.5)`,
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "10.5px",
                  letterSpacing: "0.4em",
                  textTransform: "uppercase",
                  transition: "all 0.7s ease",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = `${gateAccent}0.5)`;
                  el.style.color = `${gateAccent}0.9)`;
                  el.style.background = `${gateAccent}0.04)`;
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = `${gateAccent}0.2)`;
                  el.style.color = `${gateAccent}0.5)`;
                  el.style.background = "transparent";
                }}
              >
                {gateContent.button}
              </button>
            </div>

            {/* ループ回数に応じた追加ノート */}
            {gateContent.footerNote && (
              <p
                className="mt-6 text-xs tracking-[0.2em]"
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  color: `${gateAccent}0.2)`,
                  fontStyle: "italic",
                  opacity: inView ? 1 : 0,
                  transition: "opacity 2s ease 1.6s",
                }}
              >
                {gateContent.footerNote}
              </p>
            )}

            {/* ループ数インジケーター */}
            {loopCount > 0 && (
              <div
                className="mt-6 flex items-center justify-center gap-2"
                style={{
                  opacity: inView ? 1 : 0,
                  transition: "opacity 2s ease 1.8s",
                }}
              >
                {Array.from({ length: Math.min(loopCount, 5) }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      width: "3px",
                      height: "3px",
                      borderRadius: "50%",
                      background: `${gateAccent}0.45)`,
                      boxShadow: `0 0 4px ${gateAccent}0.25)`,
                    }}
                  />
                ))}
              </div>
            )}

            {/* Footer */}
            <div
              className="mt-8 md:mt-10 text-xs tracking-[0.2em] md:tracking-[0.28em]"
              style={{
                color: `${gateAccent}0.1)`,
                fontFamily: "'Cormorant Garamond', serif",
                fontStyle: "italic",
                opacity: inView ? 1 : 0,
                transition: "opacity 2s ease 2s",
              }}
            >
              REALMS LUXE MINERALS — 6 KINGDOMS
            </div>
          </>
        ) : (
          <>
            {/* ── 認証済み会員向けCTA ── */}

            {/* 会員名・ランク表示 */}
            {memberName && (
              <div
                className="mb-6 md:mb-8"
                style={{
                  opacity: inView ? 1 : 0,
                  transition: "opacity 1.5s ease",
                }}
              >
                <div
                  className="inline-flex items-center gap-3 px-5 py-2"
                  style={{
                    border: "1px solid rgba(201,168,76,0.08)",
                    background: "rgba(201,168,76,0.02)",
                  }}
                >
                  <div
                    style={{
                      width: "3px",
                      height: "3px",
                      borderRadius: "50%",
                      background: "rgba(201,168,76,0.35)",
                      boxShadow: "0 0 6px rgba(201,168,76,0.2)",
                    }}
                  />
                  <span
                    style={{
                      fontFamily: "'Cormorant Garamond', serif",
                      color: "rgba(201,168,76,0.28)",
                      fontSize: "9px",
                      letterSpacing: "0.3em",
                      fontStyle: "italic",
                    }}
                  >
                    {memberName}
                    {memberTier ? ` — ${memberTier}` : ""}
                  </span>
                  <div
                    style={{
                      width: "3px",
                      height: "3px",
                      borderRadius: "50%",
                      background: "rgba(201,168,76,0.35)",
                      boxShadow: "0 0 6px rgba(201,168,76,0.2)",
                      animation: "guardPulse 2.5s ease-in-out infinite 0.8s",
                    }}
                  />
                </div>
              </div>
            )}

            {/* Eyebrow */}
            <div
              className="text-xs tracking-[0.4em] md:tracking-[0.5em] uppercase mb-6 md:mb-8"
              style={{
                color: `${accentColor}0.3)`,
                fontFamily: "'Cormorant Garamond', serif",
                opacity: inView ? 1 : 0,
                transition: "opacity 1.5s ease",
              }}
            >
              {content.eyebrow}
            </div>

            {/* Heading */}
            <h2
              className="text-2xl md:text-4xl font-light mb-5 md:mb-6 leading-tight text-white/75"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                opacity: inView ? 1 : 0,
                transform: inView ? "translateY(0)" : "translateY(16px)",
                transition: "opacity 1.8s ease 0.3s, transform 1.8s ease 0.3s",
              }}
            >
              {content.heading}
            </h2>

            {/* 縦線 */}
            <div
              className="w-px h-8 md:h-10 mx-auto mb-6 md:mb-8"
              style={{
                background: `linear-gradient(to bottom, ${accentColor}0.25), transparent)`,
                opacity: inView ? 1 : 0,
                transition: "opacity 2s ease 0.7s",
              }}
            />

            {/* Sub */}
            <p
              className="text-xs tracking-[0.15em] md:tracking-[0.18em] leading-loose mb-6 md:mb-8 text-white/20"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                opacity: inView ? 1 : 0,
                transition: "opacity 2s ease 0.9s",
              }}
            >
              {content.sub}
            </p>

            {/* アセンション段階のメッセージ */}
            {isAscension && (
              <div
                className="mb-8"
                style={{
                  opacity: inView ? 1 : 0,
                  transition: "opacity 2s ease 1.1s",
                }}
              >
                <p
                  style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    color: "rgba(255,235,160,0.3)",
                    fontSize: "10.5px",
                    letterSpacing: "0.2em",
                    lineHeight: 2.2,
                    fontStyle: "italic",
                  }}
                >
                  困難もリスクも、すべて宇宙の意思の中にある。
                  <br />
                  <span style={{ color: "rgba(255,235,160,0.48)" }}>
                    あなたは、アセンション・リーダーだ。
                  </span>
                </p>
              </div>
            )}

            {/* 守護バッジ（深い段階） */}
            {isDeep && !isAscension && (
              <div
                className="mb-8"
                style={{
                  opacity: inView ? 1 : 0,
                  transition: "opacity 2s ease 1.1s",
                }}
              >
                <div
                  className="inline-flex items-center gap-2.5 px-4 py-1.5"
                  style={{ border: "1px solid rgba(201,168,76,0.1)", background: "rgba(201,168,76,0.02)" }}
                >
                  <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: "rgba(255,210,70,0.5)", boxShadow: "0 0 6px rgba(255,200,60,0.35)", animation: "guardPulse 2.5s ease-in-out infinite" }} />
                  <span style={{ fontFamily: "'Cormorant Garamond', serif", color: "rgba(255,220,140,0.35)", fontSize: "9px", letterSpacing: "0.28em", fontStyle: "italic" }}>
                    宇宙の意思が、あなたを見守っている
                  </span>
                  <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: "rgba(255,210,70,0.5)", boxShadow: "0 0 6px rgba(255,200,60,0.35)", animation: "guardPulse 2.5s ease-in-out infinite 0.8s" }} />
                </div>
              </div>
            )}

            {/* ボタン */}
            <div
              style={{
                opacity: inView ? 1 : 0,
                transition: "opacity 2s ease 1.3s",
              }}
            >
              <button
                onClick={handleButtonClick}
                className="cursor-pointer whitespace-nowrap bg-transparent"
                style={{
                  border: `1px solid ${accentColor}0.2)`,
                  padding: "10px 28px",
                  color: `${accentColor}0.5)`,
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: "10.5px",
                  letterSpacing: "0.4em",
                  textTransform: "uppercase",
                  transition: "all 0.7s ease",
                }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = `${accentColor}0.45)`;
                  el.style.color = `${accentColor}0.85)`;
                  el.style.background = `${accentColor}0.04)`;
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget as HTMLElement;
                  el.style.borderColor = `${accentColor}0.2)`;
                  el.style.color = `${accentColor}0.5)`;
                  el.style.background = "transparent";
                }}
              >
                {content.button}
              </button>
            </div>

            {/* ZEN GENESIS セクション */}
            {showZenGenesis && (
              <div
                className="mt-12"
                style={{
                  opacity: inView ? 1 : 0,
                  transition: "opacity 2s ease 1.5s",
                }}
              >
                <div className="flex items-center gap-4 mb-8">
                  <div className="flex-1 h-px" style={{ background: "linear-gradient(to right, transparent, rgba(201,168,76,0.15))" }} />
                  <div style={{ fontFamily: "'Cormorant Garamond', serif", color: "rgba(201,168,76,0.25)", fontSize: "8.5px", letterSpacing: "0.45em", textTransform: "uppercase" }}>ORIGIN</div>
                  <div className="flex-1 h-px" style={{ background: "linear-gradient(to left, transparent, rgba(201,168,76,0.15))" }} />
                </div>
                <ZenGenesisCard inView={inView} />
              </div>
            )}

            {/* Footer */}
            {!showZenGenesis && (
              <div
                className="mt-8 md:mt-10 text-xs tracking-[0.2em] md:tracking-[0.28em]"
                style={{
                  color: `${accentColor}0.13)`,
                  fontFamily: "'Cormorant Garamond', serif",
                  fontStyle: "italic",
                  opacity: inView ? 1 : 0,
                  transition: "opacity 2s ease 1.5s",
                }}
              >
                REALMS LUXE MINERALS — 6 KINGDOMS
              </div>
            )}
          </>
        )}
      </div>

      {/* 下線 */}
      <div
        className="absolute bottom-0 left-1/2 -translate-x-1/2 w-px h-16"
        style={{ background: `linear-gradient(to top, transparent, ${isUnlocked ? accentColor : gateAccent}0.1))` }}
      />

      <style>{`
        @keyframes guardPulse {
          0%, 100% { opacity: 0.5; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.5); }
        }
        @keyframes abyssPulse {
          0%, 100% { opacity: 0.7; }
          50%      { opacity: 1; }
        }
        @keyframes abyssDot {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 1; transform: scale(1.8); box-shadow: 0 0 10px rgba(200,60,20,0.6); }
        }
      `}</style>

      {/* アセンション昇華オーバーレイ */}
      {showAscensionFinal && (
        <AscensionFinalOverlay onComplete={() => setShowAscensionFinal(false)} />
      )}
    </section>
  );
}
